import {
    DataSplit,
    GeographicPortfolio,
    BlockMath,
    InfoTooltip,
    KeyValueBox,
    MetricGrid,
    Select,
    TinyButton,
    tokens,
    TreeMapPortfolio,
} from 'ComponentLibrary'
import { EnergyEfficiencyChart } from './EnergyEfficiencyChart'
import { FuelTypeChart } from './FuelTypeChart'
import { useDashboardData } from 'ClientPortal/src/pages/Dashboard/Dashboard.data'
import { Box, SelectChangeEvent, Stack, styled } from '@mui/material'
import { ALL_PORTFOLIOS } from './useSelectedPortfolio'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { isEmpty } from 'lodash'
import { PageGridContainer, PageGridItem } from 'ComponentLibrary/src/piazza/grid/PageGrid'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { DashboardLoading } from 'ClientPortal/src/pages/Dashboard/Dashboard.load'
import { DashboardBackground } from 'ClientPortal/src/pages/Dashboard/DashboardBackground'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { ExportIcon } from '@phosphor-icons/react'
import { DashboardHistoryChart } from './DashboardHistoryChart'
import { NewsFromEinwertWidget } from './NewsFromEinwertWidget'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export function Dashboard() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tDashboard } = useTranslation('pages', { keyPrefix: 'dashboard.dashboard' })
    const locale = useCurrentResolvedLocale()
    const {
        getKeyValueData,
        getMarketDevelopment,
        getIncomeDevelopment,
        getPortfolioAssetsLocations,
        getUsageTypeSplit,
        getMarketValueBreakdown,
        getEnergyKeyValues,
        showNormalizedHistory,
        isDashboardDataPending,
        isEnergySummaryLoading,
        setSelectedPortfolio,
        selectedPortfolio,
        portfolios,
        generateValueOverviewExportFileUrl,
        isGenerateValueOverviewExportFileUrl,
    } = useDashboardData()
    const { isEnergySummaryEnabled } = useFlags()
    const { marketValue, marketRent, assets, units, vacancy, wault, isWaultUnknown, grossYield, rentableArea, actualRentalIncome } =
        getKeyValueData()
    const marketDevelopment = getMarketDevelopment()
    const incomeDevelopment = getIncomeDevelopment()
    const marketValueBreakdown = getMarketValueBreakdown()
    const portfolioAssetsLocations = getPortfolioAssetsLocations()
    const usageTypeSplit = getUsageTypeSplit()
    const energyKeyValues = getEnergyKeyValues()
    const actualPortfolios = portfolios.filter((portfolio) => portfolio.type !== 'synthetic')
    const treeMapValueLabelFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2,
    })
    const treeMapTooltipValueFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    })
    const treeMapTooltipPercentageFormatter = new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: 2,
    })
    const formatTreeMapValueLabel = (value: number) => treeMapValueLabelFormatter.format(value)
    const formatTreeMapTooltipValue = (value: number) => treeMapTooltipValueFormatter.format(value)
    const formatTreeMapTooltipPercentage = (value: number) => treeMapTooltipPercentageFormatter.format(value)

    if (isDashboardDataPending) {
        return <DashboardLoading />
    }

    return (
        <>
            <DashboardBackground />
            <PageGridContainer withBottomSpace withTopSpace withFooter>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Stack direction="row" justifyContent="space-between">
                        <Headline title={tCommon('overview')} variant="headline1" />

                        <Stack direction="row" justifyContent="space-between" gap={4} alignItems={'center'}>
                            {actualPortfolios.length > 1 && (
                                <PortfolioSelect
                                    options={[
                                        { value: ALL_PORTFOLIOS, label: tDashboard('allPortfolios') },
                                        ...actualPortfolios
                                            .filter((portfolio) => !isEmpty(portfolio.assets))
                                            .map((portfolio) => ({
                                                value: portfolio.uuid,
                                                label: portfolio.title,
                                            })),
                                    ]}
                                    value={selectedPortfolio}
                                    inputLabel={''}
                                    onChange={function (event: SelectChangeEvent<string>): void {
                                        setSelectedPortfolio(event.target.value)
                                    }}
                                    noOptionsMessage={tDashboard('noPortfoliosAvailable')}
                                    size="small"
                                />
                            )}
                            {(vacancy?.value || wault?.value || grossYield?.value || rentableArea?.value || units?.value) && (
                                <TinyButton
                                    label={tActions('export')}
                                    key={'overview-export'}
                                    leftIcon={
                                        isGenerateValueOverviewExportFileUrl ? (
                                            <LoadingIndicator height={1} isPending />
                                        ) : (
                                            <ExportIcon weight="bold" />
                                        )
                                    }
                                    onClick={() => generateValueOverviewExportFileUrl()}
                                    disabled={isGenerateValueOverviewExportFileUrl}
                                />
                            )}
                        </Stack>
                    </Stack>
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <NewsFromEinwertWidget />
                </PageGridItem>
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="264px">
                        <Stack direction="column" height="100%" justifyContent="center">
                            <MetricGrid
                                size="medium"
                                variant="spacious"
                                numColumns={3}
                                values={[
                                    { ...marketValue, subheadline: tCommon('marketValue') },
                                    { ...marketRent, subheadline: tCommon('marketRent') },
                                    { ...assets, subheadline: tCommon('assets') },
                                    { ...rentableArea, subheadline: tCommon('rentableArea') },
                                    { ...actualRentalIncome, subheadline: tCommon('contractRent') },
                                    {
                                        ...grossYield,
                                        subheadline: tCommon('grossYield'),
                                        endAdornment: (
                                            <InfoTooltip
                                                text={<BlockMath math={tDashboard('formulas.grossYield')} />}
                                                size={tokens.SizingIconS}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Stack>
                    </CardItem>
                </PageGridItem>
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="264px">
                        <DataSplit
                            title={tDashboard('rentalIncome')}
                            data={usageTypeSplit}
                            valueLabel={tCommon('rent')}
                            percentageLabel={tCommon('share')}
                            size="large"
                            emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                            remainingLegendLabel={tCommon('remaining')}
                            locale={locale}
                        />
                    </CardItem>
                </PageGridItem>

                {vacancy?.value || wault?.value || grossYield?.value || rentableArea?.value || units?.value ? (
                    <>
                        <PageGridItem columnSpan={6} columnSpanXs={4}>
                            <Stack direction="row" width="100%" gap={tokens.SizingSpacing300}>
                                <CardItem>
                                    <MetricGrid
                                        size="medium"
                                        variant="spacious"
                                        numColumns={2}
                                        values={[
                                            { value: units?.value ?? '-', subheadline: tCommon('units') },
                                            { value: units?.parkingUnits ?? '-', subheadline: tCommon('parkingSpaces') },
                                        ]}
                                    />
                                </CardItem>
                                <CardItem>
                                    <KeyValueBox
                                        label={tCommon('wault')}
                                        {...wault}
                                        valueColor={isWaultUnknown ? tokens.ColorTextSubtlest : undefined}
                                        locale={locale}
                                    />
                                </CardItem>
                                <CardItem>
                                    <KeyValueBox label={tCommon('vacancyRate')} {...vacancy} inverseTrendColor locale={locale} />
                                </CardItem>
                            </Stack>
                        </PageGridItem>
                    </>
                ) : undefined}
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="484px">
                        <DashboardHistoryChart
                            title={tDashboard('marketValueDevelopment')}
                            seriesByMode={marketDevelopment}
                            showNormalizedHistory={showNormalizedHistory}
                        />
                    </CardItem>
                </PageGridItem>
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="484px">
                        <DashboardHistoryChart
                            title={tDashboard('marketRentDevelopment')}
                            seriesByMode={incomeDevelopment}
                            showNormalizedHistory={showNormalizedHistory}
                        />
                    </CardItem>
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <CardItem height="484px">
                        <GeographicPortfolio
                            label={tDashboard('mapOverview')}
                            portfolios={portfolioAssetsLocations}
                            selectedPortfolio={selectedPortfolio === ALL_PORTFOLIOS ? undefined : selectedPortfolio}
                            language={locale}
                            popupCopy={{
                                marketValueLabel: tCommon('marketValue'),
                                valuePerSquareMeterLabel: tCommon('valuePerSquareMeter'),
                                valueTrendLabel: tCommon('valueTrend'),
                                navigateToAssetTooltip: tActions('viewAsset'),
                                navigateToAssetAriaLabel: tActions('viewAsset'),
                                assetImageAlt: tCommon('assetImageAlt'),
                            }}
                        />
                    </CardItem>
                </PageGridItem>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <CardItem height="484px">
                        <TreeMapPortfolio
                            portfolioData={marketValueBreakdown}
                            emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                            translations={{
                                title: tDashboard('marketValueShare'),
                                tooltipMarketValueLabel: tCommon('marketValue'),
                                tooltipValueTrendLabel: tCommon('valueTrend'),
                                tooltipPortfolioShareLabel: tDashboard('portfolioMarketValueShare'),
                            }}
                            formatValueLabel={formatTreeMapValueLabel}
                            formatTooltipValue={formatTreeMapTooltipValue}
                            formatTooltipPercentage={formatTreeMapTooltipPercentage}
                        />
                    </CardItem>
                </PageGridItem>
                {isEnergySummaryEnabled && !isEnergySummaryLoading && (
                    <>
                        <PageGridItem columnSpan={2} columnSpanXs={2}>
                            <CardItem>
                                <KeyValueBox label={tCommon('primaryPowerConsumption')} {...energyKeyValues?.primaryPowerConsumption} />
                            </CardItem>
                        </PageGridItem>
                        <PageGridItem columnSpan={2} columnSpanXs={2} rowSpan={2}>
                            <CardItem height="264px">
                                <EnergyEfficiencyChart
                                    data={energyKeyValues?.energyEfficiencyClass}
                                    title={tCommon('energyEfficiencyClass')}
                                />
                            </CardItem>
                        </PageGridItem>
                        <PageGridItem columnSpan={2} columnSpanXs={2} rowSpan={2}>
                            <CardItem height="264px">
                                <FuelTypeChart data={energyKeyValues?.fuelType} title={tCommon('heatingType')} />
                            </CardItem>
                        </PageGridItem>
                        <PageGridItem columnSpan={2} columnSpanXs={2}>
                            <CardItem height="120px">
                                <KeyValueBox
                                    label={tCommon('carbonDioxideEquivalent')}
                                    {...energyKeyValues?.totalCarbonDioxideEquivalent}
                                />
                            </CardItem>
                        </PageGridItem>
                    </>
                )}
            </PageGridContainer>
        </>
    )
}

const CardItem = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.secondary1.grey}`,
    padding: theme.spacing(4),
    background: theme.palette.white,
    borderRadius: '12px',
    minHeight: '120px',
    width: '100%',
}))

const PortfolioSelect = styled(Select)(({ theme }) => ({
    background: theme.palette.white,
    minWidth: '180px',
})) as typeof Select<string>
